import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { cloneDeep } from '@apollo/client/utilities';

import type { Rarity } from '#src/__generated__/globalTypes.ts';
import {
  CardQuality,
  CustomRewardExperience,
  InGameCurrency,
  So5LeaderboardType,
} from '#src/__generated__/globalTypes.ts';
import fiftyFiftyCraftClueUrl from '#src/assets/crafting-clues/best-five.webp';
import competitionCraftClueUrl from '#src/assets/crafting-clues/competition.webp';
import countryCraftClueUrl from '#src/assets/crafting-clues/nationality.webp';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';
import type { Selection, TreeSchema } from '#src/routing/Tree/index.tsx';

import type {
  getRewardImage_anyRewardConfigInterface,
  getRewardImage_anyRewardInterface,
  getTreeSchemaFromSo5Fixture_so5Fixture,
  getTreeSelection_so5League,
  mergeRewardsConfig_anyRewardConfigInterface,
  mergeRewards_anyRewardInterface,
  parseRankingRewards_so5RewardConfig,
  rewardsOverviewToSimpleRewards_rewardsOverview,
} from './__generated__/rewards.graphql.ts';
import { scarcityNames } from './cards.ts';
import { getDeliverableItemImageUrl } from './deliverableItem.ts';
import { isType, withFragments } from './gql/index.ts';
import { qualityNames } from './players.ts';
import { sortByRarity } from './scarcity.ts';
import { toSnakeCase } from './toSnakeCase.ts';
import { ALL_WHEEL_CURRENCIES } from './wheelCurrencies.ts';

type CardsPerRarity = {
  [key in Rarity]?: number;
};

interface CardsInRewards extends CardsPerRarity {
  rarities?: Rarity[];
}

type CardReward = {
  rarity: Rarity;
  nb: number;
};

const getCardRewards = (cards: CardsInRewards): CardReward[] => {
  if (!cards) {
    return [];
  }
  const result: CardReward[] = [];

  const raritiesFromKeys = Object.keys(cards).filter(
    k => k !== 'rarities'
  ) as Rarity[];

  const cardRarities = raritiesFromKeys.length
    ? raritiesFromKeys
    : (cards.rarities as Rarity[]);

  if (!cardRarities) {
    return result;
  }

  cardRarities.forEach(rarity => {
    result.push({ rarity, nb: cards[rarity] || 0 });
  });

  return result;
};

export function getRewardImageUrl(
  rewardType: 'shard' | 'wheel-ticket' | 'xp' | 'card',
  rarityOrSetSlug: Rarity | string
): string {
  return `${FRONTEND_ASSET_HOST}/rewards/${rewardType}-${rarityOrSetSlug}.png`;
}
export enum RewardType {
  CARD = 'CARD',
  CASH = 'CASH',
  PROBABILISTIC_BUNDLE = 'PROBABILISTIC_BUNDLE',
  COIN = 'COIN',
  CREDIT = 'CREDIT',
  ARENA_TICKET = 'ARENA_TICKET',
  SHARD = 'SHARD',
  LEVEL_UP = 'LEVEL_UP',
}

type RewardWithExperiencesType = CustomRewardExperience | RewardType;

type CommonSimpleReward = {
  rarity: Rarity | null;
  quality?: CardQuality | null;
  count: number;
  poolSize?: number;
  sharedPool?: boolean;
  title?: Nullable<string>;
  description?: Nullable<string>;
};

type BoxSimpleReward = CommonSimpleReward & {
  type: RewardType.PROBABILISTIC_BUNDLE;
  boxConfig?: {
    id: string;
    stars: number | null;
  };
};

type OtherSimpleReward = CommonSimpleReward & {
  type: Exclude<RewardWithExperiencesType, RewardType.PROBABILISTIC_BUNDLE>;
};

export type SimpleReward = BoxSimpleReward | OtherSimpleReward;

type FlattenRewards = {
  cash?: SimpleReward | null;
  coins?: SimpleReward | null;
  credits?: SimpleReward | null;
  cards?: SimpleReward[] | null;
  boxes?: SimpleReward[] | null;
  jerseys?: SimpleReward[] | null;
  experiences?: SimpleReward[] | null;
  merch?: SimpleReward[] | null;
  tickets?: SimpleReward[] | null;
  shards?: SimpleReward[] | null;
};

const flattenRewardsToSimpleRewards = (
  flattenRewards: FlattenRewards
): SimpleReward[] => {
  return [
    ...(flattenRewards.experiences || []),
    flattenRewards.cash?.count &&
      flattenRewards.cash?.count >= 3500 &&
      flattenRewards.cash,
    ...(flattenRewards.jerseys || []),
    ...(flattenRewards.tickets || []),
    ...(flattenRewards.cards || []),
    flattenRewards.cash?.count &&
      flattenRewards.cash?.count < 3500 &&
      flattenRewards.cash,
    flattenRewards.credits,
    ...(flattenRewards.merch || []),
    ...(flattenRewards.boxes || []),
    ...(flattenRewards.shards || []),
    flattenRewards.coins,
  ].filter(Boolean);
};

export const rewardsOverviewToSimpleRewards = withFragments(
  ({
    rewardsOverview,
    rarity,
    onePerCategoryOnly,
  }: {
    rewardsOverview: rewardsOverviewToSimpleRewards_rewardsOverview;
    rarity: Rarity | null;
    onePerCategoryOnly?: boolean;
  }): SimpleReward[] => {
    const cash =
      rewardsOverview.prizePool > 0
        ? {
            type: RewardType.CASH,
            count: rewardsOverview.prizePool,
            rarity,
          }
        : null;

    const cardRewards = getCardRewards(rewardsOverview.cards as CardsInRewards);
    const cards = sortByRarity(
      cardRewards.map(cardReward => ({
        type: RewardType.CARD,
        count: cardReward.nb,
        rarity: cardReward.rarity,
      })),
      cardReward => cardReward.rarity
    ).reverse();

    const boxes = sortByRarity(
      rewardsOverview.probabilisticBundles.rarities.map(
        ({ rarity: boxRarity, count }) => ({
          type: RewardType.PROBABILISTIC_BUNDLE,
          count,
          rarity: boxRarity,
        })
      ),
      pb => pb.rarity
    );

    const jerseys =
      rewardsOverview.deliverableItems.jersey > 0
        ? {
            type: CustomRewardExperience.JERSEY,
            count: rewardsOverview.deliverableItems.jersey,
            rarity,
          }
        : null;

    const experiences =
      rewardsOverview.deliverableItems.experience > 0
        ? {
            type: CustomRewardExperience.EVENT,
            count: rewardsOverview.deliverableItems.experience,
            rarity,
          }
        : null;

    const tickets =
      rewardsOverview.deliverableItems.tickets > 0
        ? {
            type: CustomRewardExperience.TICKET,
            count: rewardsOverview.deliverableItems.tickets,
            rarity,
          }
        : null;

    const merch =
      rewardsOverview.deliverableItems.wearable > 0
        ? {
            type: CustomRewardExperience.MERCH,
            count: rewardsOverview.deliverableItems.wearable,
            rarity,
          }
        : null;

    return flattenRewardsToSimpleRewards({
      cash,
      cards: onePerCategoryOnly ? cards.slice(0, 1) : cards,
      boxes: onePerCategoryOnly ? boxes.slice(0, 1) : boxes,
      jerseys: jerseys ? [jerseys] : null,
      tickets: tickets ? [tickets] : null,
      experiences: experiences ? [experiences] : null,
      merch: merch ? [merch] : null,
    });
  },
  {
    rewardsOverview: gql`
      fragment rewardsOverviewToSimpleRewards_rewardsOverview on RewardsOverview {
        cards
        probabilisticBundles {
          rarities {
            rarity
            count
          }
        }
        prizePool
        deliverableItems {
          jersey
          experience
          tickets
          wearable
        }
      }
    ` as TypedDocumentNode<rewardsOverviewToSimpleRewards_rewardsOverview>,
  }
);

export type WithRewardRange<T> = T & {
  startRank?: number | null;
  startPct?: number | null;
  endRank?: number | null;
  endPct?: number | null;
};

export const parseRankingRewards = withFragments(
  <T extends parseRankingRewards_so5RewardConfig>(
    rewardConfigs: T[]
  ): WithRewardRange<T>[] => {
    if (!rewardConfigs) return [];

    const rangeRewards = rewardConfigs.reduce((sum, cur) => {
      const prev = sum[sum.length - 1] || undefined;

      let rewardRange: WithRewardRange<T>;
      if (!prev) {
        rewardRange = {
          startRank: 1,
          endRank: cur.ranks || null,
          startPct: null,
          endPct: cur.rankPct || null,
          ...cur,
        };
      } else if (cur.ranks) {
        const startRank = prev.endRank ? prev.endRank + 1 : null;
        const endRank = prev.endRank ? prev.endRank + cur.ranks : null;

        rewardRange = {
          startRank,
          endRank,
          startPct: null,
          endPct: null,
          ...cur,
        };
      } else if (cur.rankPct) {
        const startRank = prev.endRank ? prev.endRank + 1 : null;
        const startPct = prev.endPct || null;

        const endPctBasedOnRank = prev.endRank ? cur.rankPct : null;
        const endPct = prev.endPct ? cur.rankPct : endPctBasedOnRank;

        rewardRange = {
          startRank,
          endRank: null,
          startPct,
          endPct,
          ...cur,
        };
      } else {
        return sum;
      }

      sum.push(rewardRange);
      return sum;
    }, [] as WithRewardRange<T>[]);

    return rangeRewards;
  },
  {
    so5RewardConfig: gql`
      fragment parseRankingRewards_so5RewardConfig on So5RewardConfig {
        id
        ranks
        rankPct
      }
    ` as TypedDocumentNode<parseRankingRewards_so5RewardConfig>,
  }
);

export const getTreeSchemaFromSo5Fixture = withFragments(
  (so5Fixture: getTreeSchemaFromSo5Fixture_so5Fixture) => {
    const schema = so5Fixture.so5Leagues
      .filter(so5League =>
        so5League.so5Leaderboards.every(
          so5Leaderboard =>
            so5Leaderboard.so5TournamentType.so5LeaderboardType !==
            So5LeaderboardType.SPECIAL_TRAINING_CENTER
        )
      )
      .reduce<{
        [key: string]: { [key: string]: string[] };
      }>((sum, cur) => {
        const cardRewards = cur.so5Leaderboards
          .flatMap(so5Leaderboard => [
            ...(so5Leaderboard.rewardsConfig.ranking || []),
            ...(so5Leaderboard.rewardsConfig.conditional || []),
          ])
          .flatMap(r => r.cards)
          .filter(Boolean);

        sum[cur.displayName] = Object.values(CardQuality).reduce(
          (acc, quality) => {
            const rarities = sortByRarity([
              ...new Set(
                cardRewards
                  .filter(rewards => rewards.quality === quality)
                  .map(rewards => rewards.rarity)
              ),
            ]).reverse();

            if (!rarities.length) {
              return acc;
            }

            return {
              ...acc,
              [qualityNames[quality]]: rarities.map(
                rarity => scarcityNames[rarity]
              ),
            };
          },
          {}
        );

        return sum;
      }, {});

    return schema;
  },
  {
    so5Fixture: gql`
      fragment getTreeSchemaFromSo5Fixture_so5Fixture on So5Fixture {
        slug
        so5Leagues {
          slug
          displayName
          so5Leaderboards {
            slug
            so5TournamentType {
              id
              so5LeaderboardType
            }
            rewardsConfig {
              ranking {
                id
                cards {
                  rarity
                  quality
                }
              }
              conditional {
                id
                cards {
                  rarity
                  quality
                }
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<getTreeSchemaFromSo5Fixture_so5Fixture>,
  }
);

export const getTreeSelection = withFragments(
  ({
    schema,
    so5Leagues,
    league,
    quality,
    rarity,
  }: {
    schema: TreeSchema;
    so5Leagues: getTreeSelection_so5League[];
    league: string | undefined;
    quality: string | undefined;
    rarity: string | undefined;
  }): Selection => {
    const so5League = so5Leagues.find(l => l.slug === league);

    const leagueKey = so5League?.displayName || Object.keys(schema)[0];
    const qualityKey =
      quality && quality.toUpperCase() in qualityNames
        ? qualityNames[quality.toUpperCase() as keyof typeof qualityNames]
        : undefined;
    const rarityKey = rarity ? scarcityNames[rarity.toLowerCase()] : undefined;

    return [leagueKey, qualityKey, rarityKey];
  },
  {
    so5League: gql`
      fragment getTreeSelection_so5League on So5League {
        slug
        displayName
      }
    ` as TypedDocumentNode<getTreeSelection_so5League>,
  }
);

export const isEnergyCurrency = (
  currency: InGameCurrency
): currency is
  | typeof InGameCurrency.LIMITED_ENERGY
  | typeof InGameCurrency.RARE_ENERGY
  | typeof InGameCurrency.SUPER_RARE_ENERGY
  | typeof InGameCurrency.UNIQUE_ENERGY => {
  return [
    InGameCurrency.LIMITED_ENERGY,
    InGameCurrency.RARE_ENERGY,
    InGameCurrency.SUPER_RARE_ENERGY,
    InGameCurrency.UNIQUE_ENERGY,
  ].includesValue(currency);
};

export const isXPCurrency = (
  currency: InGameCurrency
): currency is
  | typeof InGameCurrency.COMMON_XP
  | typeof InGameCurrency.LIMITED_XP
  | typeof InGameCurrency.RARE_XP
  | typeof InGameCurrency.SUPER_RARE_XP
  | typeof InGameCurrency.UNIQUE_XP => {
  return [
    InGameCurrency.COMMON_XP,
    InGameCurrency.LIMITED_XP,
    InGameCurrency.RARE_XP,
    InGameCurrency.SUPER_RARE_XP,
    InGameCurrency.UNIQUE_XP,
  ].includesValue(currency);
};

export const isCraftingClue = (
  currency: InGameCurrency
): currency is
  | typeof InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE
  | typeof InGameCurrency.COMPETITION_CRAFT_CLUE
  | typeof InGameCurrency.COUNTRY_CRAFT_CLUE
  | typeof InGameCurrency.DIVISION_CRAFT_CLUE
  | typeof InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE
  | typeof InGameCurrency.POSITION_CRAFT_CLUE
  | typeof InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE => {
  return [
    InGameCurrency.BEST_STAR_RANK_CRAFT_CLUE,
    InGameCurrency.COMPETITION_CRAFT_CLUE,
    InGameCurrency.COUNTRY_CRAFT_CLUE,
    InGameCurrency.DIVISION_CRAFT_CLUE,
    InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE,
    InGameCurrency.POSITION_CRAFT_CLUE,
    InGameCurrency.SPECIAL_EVENT_CRAFT_CLUE,
  ].includesValue(currency);
};

export const isWheelTicket = (
  currency: InGameCurrency
): currency is (typeof ALL_WHEEL_CURRENCIES)[number] => {
  return ALL_WHEEL_CURRENCIES.includesValue(currency);
};

export const mergeRewards = withFragments(
  <T extends mergeRewards_anyRewardInterface>(rewards_: Array<T>): T[] => {
    const rewards = rewards_ as any as mergeRewards_anyRewardInterface[];

    const shardRewardsIndexByRarity: {
      [key: string]: number;
    } = {};
    const inGameCurrencyRewardsIndexByCurrency: {
      [key: string]: number;
    } = {};
    let existingMonetaryRewardIndex: number | undefined;

    return rewards.reduce<Array<mergeRewards_anyRewardInterface>>(
      (acc, reward_) => {
        const reward = reward_;
        if (isType(reward, 'CardShardsReward')) {
          const key = reward.rarity;

          const index = shardRewardsIndexByRarity[key];

          if (index !== undefined) {
            if (acc[index].id !== 'MERGED') {
              acc[index] = {
                ...cloneDeep(acc[index]),
                id: 'MERGED',
              };
            }
            if (isType(acc[index], 'CardShardsReward')) {
              acc[index].quantity += reward.quantity;
            }

            return acc;
          }

          shardRewardsIndexByRarity[key] = acc.length;
        } else if (isType(reward, 'InGameCurrencyReward')) {
          const key = reward.config.currency;
          const index = inGameCurrencyRewardsIndexByCurrency[key];

          if (index !== undefined) {
            if (acc[index].id !== 'MERGED') {
              acc[index] = {
                ...cloneDeep(acc[index]),
                id: 'MERGED',
              };
            }
            if (isType(acc[index], 'InGameCurrencyReward')) {
              acc[index].config.amount += reward.config.amount;
            }

            return acc;
          }

          inGameCurrencyRewardsIndexByCurrency[key] = acc.length;
        } else if (isType(reward, 'MonetaryReward')) {
          if (existingMonetaryRewardIndex !== undefined) {
            if (acc[existingMonetaryRewardIndex].id !== 'MERGED') {
              acc[existingMonetaryRewardIndex] = {
                ...cloneDeep(acc[existingMonetaryRewardIndex]),
                id: 'MERGED',
              };
            }
            const mergedMonetaryReward = acc[existingMonetaryRewardIndex];
            if (isType(mergedMonetaryReward, 'MonetaryReward')) {
              mergedMonetaryReward.amount.eurCents =
                (mergedMonetaryReward.amount.eurCents ?? 0) +
                (reward.amount.eurCents ?? 0);
              mergedMonetaryReward.amount.gbpCents =
                (mergedMonetaryReward.amount.gbpCents ?? 0) +
                (reward.amount.gbpCents ?? 0);
              mergedMonetaryReward.amount.usdCents =
                (mergedMonetaryReward.amount.usdCents ?? 0) +
                (reward.amount.usdCents ?? 0);
              mergedMonetaryReward.amount.wei =
                (mergedMonetaryReward.amount.wei ?? 0n) +
                (reward.amount.wei ?? 0n);
              mergedMonetaryReward.amount.lamport =
                (mergedMonetaryReward.amount.lamport ?? 0n) +
                (reward.amount.lamport ?? 0n);

              return acc;
            }
          }

          existingMonetaryRewardIndex = acc.length;
        }
        acc.push(reward);
        return acc;
      },
      []
      // HACK make typescript understand our magic with data masking
    ) as any as T[];
  },
  {
    AnyRewardInterface: gql`
      fragment mergeRewards_anyRewardInterface on AnyRewardInterface {
        id
        ... on CardShardsReward {
          id
          rarity
          quantity
        }
        ... on InGameCurrencyReward {
          id
          config {
            id
            currency
            amount
          }
        }
        ... on MonetaryReward {
          id
          amount {
            eurCents
            gbpCents
            usdCents
            wei
            lamport
          }
        }
      }
    ` as TypedDocumentNode<mergeRewards_anyRewardInterface>,
  }
);

export const mergeRewardsConfig = withFragments(
  <T extends mergeRewardsConfig_anyRewardConfigInterface>(
    rewards: Array<T>
  ): T[] => {
    const monetaryRewards = rewards.filter(r =>
      isType(r, 'MonetaryRewardConfig')
    );
    const conversionCreditRewards = rewards.filter(r =>
      isType(r, 'ConversionCreditRewardConfig')
    );
    const cardShardRewards = rewards.filter(r =>
      isType(r, 'CardShardRewardConfig')
    );
    const inGameCurrencyRewards = rewards.filter(r =>
      isType(r, 'InGameCurrencyRewardConfig')
    );

    const otherRewards = rewards.filter(
      r =>
        !isType(r, 'MonetaryRewardConfig') &&
        !isType(r, 'ConversionCreditRewardConfig') &&
        !isType(r, 'CardShardRewardConfig') &&
        !isType(r, 'InGameCurrencyRewardConfig')
    );

    const mergedMonetaryRewards = monetaryRewards.reduce<
      Extract<T, { __typename: 'MonetaryRewardConfig' }> | undefined
    >((acc, cur) => {
      if (!acc) {
        return cur;
      }
      return {
        ...acc,
        ...{
          monetaryAmount: {
            eurCents:
              (acc.monetaryAmount.eurCents || 0) +
              (cur.monetaryAmount.eurCents || 0),
            gbpCents:
              (acc.monetaryAmount.gbpCents || 0) +
              (cur.monetaryAmount.gbpCents || 0),
            usdCents:
              (acc.monetaryAmount.usdCents || 0) +
              (cur.monetaryAmount.usdCents || 0),
            wei:
              (acc.monetaryAmount.wei || 0n) + (cur.monetaryAmount.wei || 0n),
            lamport:
              (acc.monetaryAmount.lamport || 0n) +
              (cur.monetaryAmount.lamport || 0n),
            referenceCurrency: acc.monetaryAmount.referenceCurrency,
          },
        },
      };
    }, undefined);

    const mergedConversionCreditRewards = conversionCreditRewards.reduce<
      Extract<T, { __typename: 'ConversionCreditRewardConfig' }> | undefined
    >((acc, cur) => {
      if (!acc) {
        return cur;
      }
      return {
        ...acc,
        ...{
          maxDiscount: {
            eurCents:
              (acc.maxDiscount.eurCents || 0) + (cur.maxDiscount.eurCents || 0),
            gbpCents:
              (acc.maxDiscount.gbpCents || 0) + (cur.maxDiscount.gbpCents || 0),
            usdCents:
              (acc.maxDiscount.usdCents || 0) + (cur.maxDiscount.usdCents || 0),
            wei: (acc.maxDiscount.wei || 0n) + (cur.maxDiscount.wei || 0n),
            lamport:
              (acc.maxDiscount.lamport || 0n) + (cur.maxDiscount.lamport || 0n),
            referenceCurrency: acc.maxDiscount.referenceCurrency,
          },
        },
      };
    }, undefined);

    const mergedCardShardRewards = cardShardRewards.reduce<
      Extract<T, { __typename: 'CardShardRewardConfig' }>[]
    >((acc, cur) => {
      const index = acc.findIndex(r => r.configRarity === cur.configRarity);
      if (index !== -1) {
        acc[index] = {
          ...acc[index],
          ...{ quantity: acc[index].quantity + cur.quantity },
        };
        return acc;
      }
      acc.push(cur);
      return acc;
    }, []);

    const mergedInGameCurrencyRewards = inGameCurrencyRewards.reduce<
      Extract<T, { __typename: 'InGameCurrencyRewardConfig' }>[]
    >((acc, cur) => {
      const index = acc.findIndex(r => r.currency === cur.currency);
      if (index !== -1) {
        acc[index] = {
          ...acc[index],
          ...{ amount: acc[index].amount + cur.amount },
        };
        return acc;
      }
      acc.push(cur);
      return acc;
    }, []);

    return [
      mergedMonetaryRewards,
      mergedConversionCreditRewards,
      ...mergedInGameCurrencyRewards,
      ...mergedCardShardRewards,
      ...otherRewards,
    ].filter(Boolean);
  },
  {
    anyRewardConfigInterface: gql`
      fragment mergeRewardsConfig_anyRewardConfigInterface on AnyRewardConfigInterface {
        id
        ... on CardShardRewardConfig {
          id
          configRarity
          quantity
        }
        ... on InGameCurrencyRewardConfig {
          id
          currency
          amount
        }
        ... on MonetaryRewardConfig {
          id
          monetaryAmount: amount {
            eurCents
            gbpCents
            usdCents
            wei
            lamport
            referenceCurrency
          }
        }
        ... on ConversionCreditRewardConfig {
          id
          maxDiscount {
            eurCents
            gbpCents
            usdCents
            wei
            lamport
            referenceCurrency
          }
        }
      }
    ` as TypedDocumentNode<mergeRewardsConfig_anyRewardConfigInterface>,
  }
);

export const getInGameCurrencyImageUrl = (currency: InGameCurrency) => {
  switch (currency) {
    case InGameCurrency.WHEEL_TICKET:
    case InGameCurrency.GOLDEN_WHEEL_TICKET:
    case InGameCurrency.COMMON_GOLDEN_WHEEL_TICKET:
      return `${FRONTEND_ASSET_HOST}/rewards/wheel-ticket-limited.png`;
    case InGameCurrency.COMMON_WHEEL_TICKET:
      return `${FRONTEND_ASSET_HOST}/rewards/wheel-ticket-common.png`;
    case InGameCurrency.RARE_WHEEL_TICKET:
    case InGameCurrency.RARE_GOLDEN_WHEEL_TICKET:
      return `${FRONTEND_ASSET_HOST}/rewards/wheel-ticket-rare.png`;
    case InGameCurrency.SUPER_RARE_WHEEL_TICKET:
    case InGameCurrency.SUPER_RARE_GOLDEN_WHEEL_TICKET:
      return `${FRONTEND_ASSET_HOST}/rewards/wheel-ticket-super_rare.png`;
    case InGameCurrency.UNIQUE_WHEEL_TICKET:
    case InGameCurrency.UNIQUE_GOLDEN_WHEEL_TICKET:
      return `${FRONTEND_ASSET_HOST}/rewards/wheel-ticket-unique.png`;
    case InGameCurrency.OLD_CLUB_SHOP_COIN:
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/coin.png`;
    case InGameCurrency.COMMON_XP:
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-common.png`;
    case InGameCurrency.LIMITED_XP:
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-limited.png`;
    case InGameCurrency.RARE_XP:
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-rare.png`;
    case InGameCurrency.SUPER_RARE_XP:
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-super_rare.png`;
    case InGameCurrency.UNIQUE_XP:
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/xp-unique.png`;
    case InGameCurrency.COUNTRY_CRAFT_CLUE:
      return countryCraftClueUrl;
    case InGameCurrency.COMPETITION_CRAFT_CLUE:
      return competitionCraftClueUrl;
    case InGameCurrency.FIFTY_FIFTY_CRAFT_CLUE:
      return fiftyFiftyCraftClueUrl;
    case InGameCurrency.COMMON_GEM:
      return `${FRONTEND_ASSET_HOST}/rewards/common-gem.png`;
    default:
      return '';
  }
};

export const getRewardImage = withFragments(
  (
    rewardOrRewardConfig_:
      | getRewardImage_anyRewardConfigInterface
      | getRewardImage_anyRewardInterface
  ) => {
    const rewardOrRewardConfig = rewardOrRewardConfig_;
    if (
      isType(rewardOrRewardConfig, 'MonetaryRewardConfig') ||
      isType(rewardOrRewardConfig, 'MonetaryReward')
    ) {
      return `${FRONTEND_ASSET_HOST}/rewards/cash.png`;
    }
    if (
      (isType(rewardOrRewardConfig, 'CardShardRewardConfig') ||
        isType(rewardOrRewardConfig, 'CardShardsReward')) &&
      rewardOrRewardConfig.cardSet?.slug
    ) {
      return `${FRONTEND_ASSET_HOST}/rewards/shard-${rewardOrRewardConfig.cardSet?.slug}.png`;
    }
    if (isType(rewardOrRewardConfig, 'CardShardRewardConfig')) {
      const { configRarity } = rewardOrRewardConfig;
      return `${FRONTEND_ASSET_HOST}/rewards/shard-${configRarity}.png`;
    }
    if (isType(rewardOrRewardConfig, 'CardShardsReward')) {
      const { rarity } = rewardOrRewardConfig;
      return `${FRONTEND_ASSET_HOST}/rewards/shard-${rarity}.png`;
    }
    if (
      isType(rewardOrRewardConfig, 'ShopItemRewardConfig') ||
      isType(rewardOrRewardConfig, 'ShopItemReward')
    ) {
      return rewardOrRewardConfig.shopItem.pictureUrl;
    }
    if (isType(rewardOrRewardConfig, 'ExperienceRewardConfig')) {
      return rewardOrRewardConfig.pictureUrl || '';
    }
    if (isType(rewardOrRewardConfig, 'ExperienceReward')) {
      return rewardOrRewardConfig.config.pictureUrl || '';
    }
    if (
      isType(rewardOrRewardConfig, 'InGameCurrencyRewardConfig') ||
      isType(rewardOrRewardConfig, 'InGameCurrencyReward')
    ) {
      const config = isType(rewardOrRewardConfig, 'InGameCurrencyRewardConfig')
        ? rewardOrRewardConfig
        : rewardOrRewardConfig.config;
      return getInGameCurrencyImageUrl(config.currency);
    }
    if (isType(rewardOrRewardConfig, 'CardPacksRewardConfig')) {
      return rewardOrRewardConfig.cardPack?.pictureUrl || '';
    }
    if (
      isType(rewardOrRewardConfig, 'CoinRewardConfig') ||
      isType(rewardOrRewardConfig, 'CoinReward')
    ) {
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/coin.png`;
    }
    if (
      isType(rewardOrRewardConfig, 'ConversionCreditRewardConfig') ||
      isType(rewardOrRewardConfig, 'AnyConversionCreditReward')
    ) {
      return `${FRONTEND_ASSET_HOST}/rewards/credit.png`;
    }
    if (isType(rewardOrRewardConfig, 'ProbabilisticBundleRewardConfig')) {
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/box-${toSnakeCase(rewardOrRewardConfig.probabilisticBundleConfig.rarity)}.png`;
    }
    if (isType(rewardOrRewardConfig, 'ProbabilisticBundleReward')) {
      return `${FRONTEND_ASSET_HOST}/football/rewards/icons/box-${toSnakeCase(rewardOrRewardConfig.config.probabilisticBundleConfig.rarity)}.png`;
    }
    if (isType(rewardOrRewardConfig, 'DeliverableItemRewardConfig')) {
      return getDeliverableItemImageUrl(rewardOrRewardConfig.deliverableItem);
    }
    return '';
  },
  {
    anyRewardInterface: gql`
      fragment getRewardImage_anyRewardInterface on AnyRewardInterface {
        id
        ... on CardShardsReward {
          id
          rarity
          cardSet {
            slug
          }
        }
        ... on ShopItemReward {
          id
          shopItem {
            id
            pictureUrl
          }
        }
        ... on ExperienceReward {
          id
          config {
            id
            pictureUrl
          }
        }
        ... on InGameCurrencyReward {
          id
          config {
            id
            currency
          }
        }
        ... on ProbabilisticBundleReward {
          id
          config {
            id
            probabilisticBundleConfig {
              id
              rarity
            }
          }
        }
        ... on DeliverableItemReward {
          id
          deliverableItem {
            slug
          }
        }
      }
    ` as TypedDocumentNode<getRewardImage_anyRewardInterface>,
    anyRewardConfigInterface: gql`
      fragment getRewardImage_anyRewardConfigInterface on AnyRewardConfigInterface {
        id
        ... on CardShardRewardConfig {
          id
          configRarity
          cardSet {
            slug
          }
        }
        ... on ShopItemRewardConfig {
          id
          shopItem {
            id
            pictureUrl
          }
        }
        ... on ExperienceRewardConfig {
          id
          pictureUrl
        }
        ... on InGameCurrencyRewardConfig {
          id
          currency
        }
        ... on CardPacksRewardConfig {
          id
          cardPack {
            slug
            pictureUrl
          }
        }
        ... on ProbabilisticBundleRewardConfig {
          id
          probabilisticBundleConfig {
            id
            rarity
          }
        }
        ... on DeliverableItemRewardConfig {
          id
          deliverableItem {
            slug
            ...getDeliverableItemImageUrl_deliverableItem
          }
        }
      }
      ${getDeliverableItemImageUrl.fragments.deliverableItem}
    ` as TypedDocumentNode<getRewardImage_anyRewardConfigInterface>,
  }
);
